import React, { useEffect } from 'react';
import { Form as FinalForm } from 'react-final-form';
import { Alert, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { t, translate } from 'react-switch-lang';
import { Link } from 'react-router-dom';
import './contactusform.scss';
import './ContactUsPaySimply.scss';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// eslint-disable-next-line import/no-unresolved
import { faMobileAlt, faWindow } from '@awesome.me/kit-299dd46f46/icons/classic/regular';
import {
  validateBody,
  validateMaxCharacter,
  validateName,
  validateTransactionNumber,
} from './Validation';
import { invalidFormAlert, setScreenReaderAlert } from '../../../helperFunctions/Accessibility';
import RelevantFAQ from './RelevantFAQ';
import ContactFormHeader from './ContactFormHeader';
import useRelevantFAQSearch from '../../../helperFunctions/useRelevantFAQSearch';
import useContactFormSubmit from '../../../helperFunctions/useContactFormSubmit';
import {
  MailcheckEmailField,
  ContactRadioField,
  SelectField,
  TextAreaField,
  TextField,
} from '../../../components/FormFields/FormFields';
import ContactReCaptcha from '../../../components/ContactReCaptcha';
import { getContactFormStyle } from '../../../configurations/Brands';
import {
  events,
  trackGoogleAnalyticsEvent,
} from '../../../helperFunctions/GoogleAnalytics';

const WEB_APPLICATION = 'Web';
const MOBILE_APPLICATION = 'Mobile';
const PAYMENT_VERIFICATION_CATEGORY = 'Payment Verification';
const PAYMENT_ISSUE_CATEGORY = 'Payment Issue';
const MISSING_RECEIPT_CATEGORY = 'Missing Receipt';
const REFUND_CATEGORY = 'Refund';
const ACCOUNT_DELETION_CATEGORY = 'Data or account deletion request';
const OTHER_CATEGORY = 'Other';

function isSubmitCategory(category) {
  return [
    PAYMENT_ISSUE_CATEGORY,
    ACCOUNT_DELETION_CATEGORY,
    OTHER_CATEGORY,
  ].includes(category);
}

function ContactUsPaySimply({ widgetMode, removeAccount }) {
  const { lang } = useSelector((state) => state);
  const {
    clearSearch,
    handleTextareaChange,
    searchResults,
  } = useRelevantFAQSearch({
    brand: 'paysimply',
    lang,
  });
  const {
    dismissError,
    error,
    errorMessageRef,
    reCaptchaRef,
    showReCaptchaError,
    showError,
    submitted,
    submitContactRequest,
    successMessageRef,
  } = useContactFormSubmit();

  const isMobile = (window.location.href.includes('mobileapp') ? '?mobileapp' : '');
  const initialValues = removeAccount ? {
    application: MOBILE_APPLICATION,
    category: ACCOUNT_DELETION_CATEGORY,
  } : {};

  useEffect(() => {
    trackGoogleAnalyticsEvent(events.CONTACT_FORM_VIEW, {
      brand: 'paysimply',
      language: lang,
    });
  }, [lang]);

  function validateContactForm(values) {
    const errors = {};

    if (!values.application) errors.application = t('Validation_Required');
    if (!values.category) errors.category = t('Validation_Required');

    if (isSubmitCategory(values.category)) {
      const nameError = validateName(values.name || '');
      const descriptionError = validateBody(values.description || '');
      if (nameError) errors.name = nameError;
      if (descriptionError) errors.description = descriptionError;
    }

    if (values.category === PAYMENT_ISSUE_CATEGORY) {
      const transactionNumberError = validateTransactionNumber(values.transactionNumber || '');
      const errorMessageError = validateMaxCharacter(values.paySimplyErrorMessage || '', 100);

      if (!values.paymentOption) errors.paymentOption = t('Validation_Required');
      if (transactionNumberError) errors.transactionNumber = transactionNumberError;
      if (errorMessageError) errors.paySimplyErrorMessage = errorMessageError;
    }

    return errors;
  }

  function getCategoryLabel(category) {
    switch (category) {
      case PAYMENT_VERIFICATION_CATEGORY:
        return t('ContactForm_Category_PaySimply_PaymentVerification');
      case PAYMENT_ISSUE_CATEGORY:
        return t('ContactForm_Category_PaySimply_PaymentIssue');
      case MISSING_RECEIPT_CATEGORY:
        return t('ContactForm_Category_PaySimply_MissingReceipt');
      case REFUND_CATEGORY:
        return t('Contact_Paysimply_Category_Refund');
      case ACCOUNT_DELETION_CATEGORY:
        return t('ContactForm_Category_All_AccountDeletion');
      case OTHER_CATEGORY:
        return t('ContactForm_PaymentOption_Other');
      default:
        return '';
    }
  }

  function handleCategoryChange(category, input) {
    input.onChange(category);
    input.onBlur();

    if (
      category === PAYMENT_VERIFICATION_CATEGORY ||
      category === MISSING_RECEIPT_CATEGORY
    ) {
      clearSearch();
    }

    setScreenReaderAlert(
      t('ScreenReaderAlert_SelectedOption').replace('xOption', getCategoryLabel(category))
    );
  }

  async function sendAPI(values) {
    if (!isSubmitCategory(values.category)) return false;

    const data = {
      Brand: 'PaySimply',
      Application: values.application,
      BrandID: values.application === WEB_APPLICATION ?
        'PaySimply website' :
        'PaySimply mobile app',
      CustomerEmail: values.customerEmail,
      Language: lang,
      Category: values.category,
      Body: values.description,
      Name: values.name,
    };

    if (values.category === PAYMENT_ISSUE_CATEGORY) {
      data.PaymentOption = values.paymentOption;
    }

    if (values.category === PAYMENT_ISSUE_CATEGORY && values.transactionNumber) {
      data.TransactionNumber = values.transactionNumber;
    }

    if (values.category === PAYMENT_ISSUE_CATEGORY && values.paySimplyErrorMessage) {
      data.ErrorMessage = values.paySimplyErrorMessage;
    }

    return submitContactRequest(data);
  }

  useEffect(() => {
    if (widgetMode) {
      document.getElementById('desktop-contact-us').style.padding = '0px';
    }
  }, []);

  return (
    <>
      {showError && (
        <Alert
          ref={errorMessageRef}
          variant="danger"
          id="error_message"
          className="mt-3"
          onClose={dismissError}
          dismissible
        >
          <p style={{ marginBottom: 0 }}>{error}</p>
        </Alert>
      )}
      {submitted && (
        <div
          id="successMessage"
          ref={successMessageRef}
          style={{ display: 'block', visibility: 'visible' }}
        >
          <h1>{t('ContactForm_Submitted')}</h1>
          <Link to={(widgetMode) ? `/widget/paysimply/${lang}${isMobile}` : `/paysimply/${lang}/categories`}>
            <Button className="error-go-home-btn paysimply-button">{t('ReturnHome_Btn')}</Button>
          </Link>
        </div>
      )}
      {!submitted && (
        <div
          id="desktop-contact-us"
          className={widgetMode && 'widget-mode-form'}
          style={getContactFormStyle('paysimply')}
        >
          <div id="skip-link-content">
            <ContactFormHeader brand="paysimply" lang={lang} widgetMode={widgetMode} />
            <p className="contact-label-suggestion">{t('ContactForm_Fields_Required')}</p>
            <FinalForm
              initialValues={initialValues}
              onSubmit={sendAPI}
              validate={validateContactForm}
              render={({
                handleSubmit,
                invalid,
                errors,
                submitting,
                values,
                submitFailed,
                touched,
              }) => {
                const isPaymentIssue = values.category === PAYMENT_ISSUE_CATEGORY;
                const showPaymentStatus = values.category === PAYMENT_VERIFICATION_CATEGORY;
                const showResendReceipt = values.category === MISSING_RECEIPT_CATEGORY;
                const showRefundDialog = values.category === REFUND_CATEGORY;
                const showSubmitFields = isSubmitCategory(values.category);
                const applicationError = errors.application;
                const showApplicationError = (
                  touched?.application || submitFailed
                ) && applicationError;
                const categoryError = errors.category;
                const showCategoryError = (
                  touched?.category || submitFailed
                ) && categoryError;

                return (
                  <form
                    onSubmit={(event) => {
                      if (!showSubmitFields) {
                        event.preventDefault();
                        invalidFormAlert(invalid, errors);
                        return;
                      }

                      invalidFormAlert(invalid, errors);
                      handleSubmit(event);
                    }}
                    noValidate
                  >
                    <div className="row">
                      <MailcheckEmailField
                        className="col-12 col-md-6"
                        id="ContactForm_General_Email_"
                        name="customerEmail"
                        label={t('ContactForm_General_Email_Label')}
                        formatText={t('ContactForm_General_Email_Help')}
                      />
                    </div>

                    <fieldset className="paysimply-radio form-field">
                      <legend
                        id="applicationLabel"
                        className={`field-label ${showApplicationError ? 'is-invalid' : ''}`}
                      >
                        {t('ContactForm_General_AppID_Label')}
                      </legend>
                      <div className="radio-container">
                        <ContactRadioField
                          name="application"
                          value={MOBILE_APPLICATION}
                          content={(
                            <>
                              <FontAwesomeIcon icon={faMobileAlt} className="btn-icon" />
                              {t('ContactForm_AppID_Mobile')}
                            </>
                          )}
                        />
                        <ContactRadioField
                          name="application"
                          value={WEB_APPLICATION}
                          content={(
                            <>
                              <FontAwesomeIcon icon={faWindow} className="btn-icon" />
                              {t('ContactForm_AppID_Website')}
                            </>
                          )}
                        />
                      </div>
                      {showApplicationError && (
                        <span className="errorMessage errorMessageBlock" id="app_error_message">{applicationError}</span>
                      )}
                    </fieldset>

                    <fieldset className="paysimply-radio form-field">
                      <legend
                        id="categoryLabel"
                        className={`field-label ${showCategoryError ? 'is-invalid' : ''}`}
                      >
                        {t('ContactForm_General_Category_Label')}
                      </legend>

                      <div className="row no-gutters w-100">
                        <div className="col-12 col-md-6 mb-2 pr-md-1">
                          <ContactRadioField
                            name="category"
                            value={PAYMENT_VERIFICATION_CATEGORY}
                            className="w-100 h-100"
                            optionClassName="d-block w-100 h-100"
                            content={t('ContactForm_Category_PaySimply_PaymentVerification')}
                            onChange={handleCategoryChange}
                          />
                        </div>

                        <div className="col-12 col-md-6 mb-2 pl-md-1">
                          <ContactRadioField
                            name="category"
                            value={PAYMENT_ISSUE_CATEGORY}
                            className="w-100 h-100"
                            optionClassName="d-block w-100 h-100"
                            content={t('ContactForm_Category_PaySimply_PaymentIssue')}
                            onChange={handleCategoryChange}
                          />
                        </div>
                        <div className="col-12 col-md-6 mb-2 pr-md-1">
                          <ContactRadioField
                            name="category"
                            value={MISSING_RECEIPT_CATEGORY}
                            className="w-100 h-100"
                            optionClassName="d-block w-100 h-100"
                            content={t('ContactForm_Category_PaySimply_MissingReceipt')}
                            onChange={handleCategoryChange}
                          />
                        </div>

                        <div className="col-12 col-md-6 mb-2 pl-md-1">
                          <ContactRadioField
                            name="category"
                            value={REFUND_CATEGORY}
                            className="w-100 h-100"
                            optionClassName="d-block w-100 h-100"
                            content={t('Contact_Paysimply_Category_Refund')}
                            onChange={handleCategoryChange}
                          />
                        </div>

                        <div className="col-12 col-md-6 mb-2 pr-md-1">
                          <ContactRadioField
                            name="category"
                            value={ACCOUNT_DELETION_CATEGORY}
                            className="w-100 h-100"
                            optionClassName="d-block w-100 h-100"
                            content={t('ContactForm_Category_All_AccountDeletion')}
                            onChange={handleCategoryChange}
                          />
                        </div>

                        <div className="col-12 col-md-6 mb-2 pl-md-1">
                          <ContactRadioField
                            name="category"
                            value={OTHER_CATEGORY}
                            className="w-100 h-100"
                            optionClassName="d-block w-100 h-100"
                            content={t('ContactForm_PaymentOption_Other')}
                            onChange={handleCategoryChange}
                          />
                        </div>
                      </div>

                      {showCategoryError && (
                        <span className="errorMessage" id="category_error_message">{categoryError}</span>
                      )}
                    </fieldset>

                    {showPaymentStatus && (
                      <div className="div-check-status">
                        <div id="status-top">
                          <h2>{t('Contact_Paysimply_CheckStatusOnlineTitle')}</h2>
                          <span>{t('Contact_Paysimply_CheckStatusOnlineText')}</span>
                        </div>
                        <div id="status-bottom">
                          <a
                            href={t('Link_PaySimply_MyPayment')}
                            target="_blank"
                            rel="noreferrer"
                            className="self-serve-btn btn"
                            aria-label={t('Contact_Paysimply_CheckStatusOnlineBtnCheckPayStatus')}
                          >
                            {t('Contact_Paysimply_CheckStatusOnlineBtnCheckPayStatus')}
                          </a>
                        </div>
                      </div>
                    )}

                    {showRefundDialog && (
                      <div className="div-check-status">
                        <div id="status-top">
                          <h2>{t('Contact_Paysimply_RefundDialogTitle')}</h2>
                          <span dangerouslySetInnerHTML={{ __html: t('Contact_Paysimply_RefundDialogContent') }} />
                        </div>
                      </div>
                    )}

                    {showResendReceipt && (
                      <div className="div-check-status">
                        <div id="status-top">
                          <h2>{t('Contact_PaySimply_ResendReceiptTitle')}</h2>
                          <span>{t('Contact_PaySimply_ResendReceiptText')}</span>
                        </div>
                        <div id="status-bottom">
                          <a
                            href={t('Link_PaySimply_MyPayment')}
                            target="_blank"
                            rel="noreferrer"
                            className="self-serve-btn btn"
                            aria-label={t('Contact_PaySimply_ResendReceipt_LinkDescription')}
                          >
                            {t('Contact_Paysimply_ResendReceiptBtnTitle')}
                          </a>
                        </div>
                      </div>
                    )}

                    {showSubmitFields && (
                      <>
                        <div className="row">
                          <TextField
                            className="col-12 col-md-6"
                            id="ContactForm_General_Name_"
                            name="name"
                            label={t('ContactForm_Name')}
                          />
                        </div>

                        {isPaymentIssue && (
                          <>
                            <div className="row">
                              <SelectField
                                className="col-12 col-md-6"
                                id="ContactForm_PaymentOption_"
                                name="paymentOption"
                                label={t('ContactForm_General_PaymentOption_Label')}
                                placeholder={t('Contact_Paysimply_SelectPlaceholder')}
                                options={[
                                  {
                                    label: t('ContactForm_PaymentOption_CreditOrDebit'),
                                    value: 'Credit or Debit Card',
                                  },
                                  {
                                    label: t('ContactForm_PaymentOption_eTransfer'),
                                    value: 'Interac eTransfer',
                                  },
                                  {
                                    label: t('ContactForm_PaymentOption_PayPal'),
                                    value: 'PayPal',
                                  },
                                  {
                                    label: t('ContactForm_PaymentOption_InPerson'),
                                    value: 'In person at Canada Post',
                                  },
                                  {
                                    label: t('ContactForm_PaymentOption_Other'),
                                    value: 'Other',
                                  },
                                ]}
                              />
                              <TextField
                                className="col-12 col-md-6"
                                id="ContactForm_General_TransactionNumber_"
                                name="transactionNumber"
                                label={(
                                  <>
                                    {t('Contact_Paysimply_TransactionNumberTitle')}
                                    <span className="optional-lbl">
                                      {`(${t('ContactForm_Optional')})`}
                                    </span>
                                    <OverlayTrigger
                                      placement="right"
                                      overlay={(
                                        <Tooltip
                                          id="transaction-number-help-tooltip"
                                          className="field-tooltip"
                                        >
                                          {t('Contact_Paysimply_SuccessPaymentHelpText')}
                                        </Tooltip>
                                      )}
                                    >
                                      <span
                                        className="field-tooltip-icon"
                                        role="button"
                                        tabIndex="0"
                                        aria-label={t('Contact_Paysimply_SuccessPaymentHelpText')}
                                      >
                                        ?
                                      </span>
                                    </OverlayTrigger>
                                  </>
                                )}
                                maxLength={13}
                                parse={(value) => (value || '').replace(/\D/g, '')}
                              />
                            </div>

                            <div className="row">
                              <TextField
                                className="col-12"
                                id="ContactForm_General_ErrorMessage_"
                                name="paySimplyErrorMessage"
                                label={t('Contact_Paysimply_ErrorMessageOnPaySimply')}
                                formatText={t('Contact_Paysimply_ErrorMessageOnPaySimplyHelpText')}
                                optional
                              />
                            </div>
                          </>
                        )}

                        <div className="row">
                          <TextAreaField
                            className="col-12"
                            id="ContactForm_General_Body_"
                            name="description"
                            label={t('ContactForm_General_Body_Label')}
                            rows={8}
                            onChange={handleTextareaChange}
                          >
                            <div className="sensitive-information-disclaimer">
                              {t('Sensitive_Information_Disclaimer')}
                            </div>
                          </TextAreaField>
                        </div>

                        <RelevantFAQ searchResults={searchResults} brand="paysimply" lang={lang} />
                        <Button
                          id="submit-button"
                          className="paysimply-button"
                          type="submit"
                          disabled={submitting}
                          size="lg"
                          style={{ backgroundColor: '#2B4E7B' }}
                        >
                          {submitting ? t('ContactForm_Submitting') : t('ContactForm_Submit_button')}
                        </Button>
                        <ContactReCaptcha ref={reCaptchaRef} onErrored={showReCaptchaError} />
                      </>
                    )}
                  </form>
                );
              }}
            />

            <Alert className="mt-3 email-banner" show={process.env.REACT_APP_ENV === 'dev'}>
              On the test system, contact form emails will be sent to
              <strong> psidevstar@gmail.com</strong>
            </Alert>
          </div>
        </div>
      )}
    </>
  );
}

export default translate(ContactUsPaySimply);
